import React from "react";
import { Link } from "react-router-dom";
import { HeartPulse, Dumbbell, Activity, Sparkles, Flame, Check, ArrowUpRight } from "lucide-react";

const IMG = {
  physio: "https://images.pexels.com/photos/13538710/pexels-photo-13538710.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  fitness: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxneW0lMjBmaXRuZXNzJTIwdHJhaW5pbmd8ZW58MHx8fHwxNzgzOTU4ODA0fDA&ixlib=rb-4.1.0&q=85",
  rehab: "https://images.unsplash.com/photo-1645005513713-9e2b92a687d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxyZWhhYmlsaXRhdGlvbiUyMGV4ZXJjaXNlfGVufDB8fHx8MTc4Mzk1ODgwNHww&ixlib=rb-4.1.0&q=85",
  spa: "https://images.unsplash.com/photo-1519415510236-718bdfcd89c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxmb290JTIwc3BhfGVufDB8fHx8MTc4Mzk1ODgwNHww&ixlib=rb-4.1.0&q=85",
  cupping: "https://images.pexels.com/photos/8312856/pexels-photo-8312856.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const LIST = [
  { key: "physio", icon: HeartPulse, title: "Physiotherapy", img: IMG.physio,
    desc: "Hands-on musculoskeletal care. We assess pain triggers, movement patterns and posture, and treat with manual therapy, dry needling and guided exercise.",
    points: ["Back & neck pain relief", "Sports injury recovery", "Post-natal & pelvic care", "Dry needling & manual therapy"] },
  { key: "fitness", icon: Dumbbell, title: "Fitness", img: IMG.fitness,
    desc: "Personalised strength and conditioning designed to complement your recovery or fitness goal. Small-group, one-on-one, or hybrid formats.",
    points: ["Strength & mobility", "Weight & fat loss", "Sport-specific training", "Corrective exercise"] },
  { key: "rehab", icon: Activity, title: "Rehab", img: IMG.rehab,
    desc: "Structured, milestone-driven programs for post-surgical or post-injury clients. We work with your surgeon or referring doctor for continuity.",
    points: ["Post-surgical protocol", "ACL / shoulder / spine rehab", "Return-to-sport testing", "Chronic pain programs"] },
  { key: "spa", icon: Sparkles, title: "Foot Spa", img: IMG.spa,
    desc: "Restorative foot care combining warmth, hydro-therapy, reflexology and soft-tissue release. Ideal for tired feet, plantar issues and stress relief.",
    points: ["Reflexology", "Warm water therapy", "Plantar fasciitis care", "Deep relaxation"] },
  { key: "cupping", icon: Flame, title: "Cupping", img: IMG.cupping,
    desc: "Traditional and modern cupping therapy to release deep muscle tension, improve circulation, and accelerate recovery from training or injury.",
    points: ["Dry & fire cupping", "Trigger-point release", "Improved circulation", "Post-workout recovery"] },
];

export default function Services() {
  return (
    <main data-testid="services-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="eyebrow">Services</div>
      <h1 className="mt-3 font-heading text-5xl sm:text-6xl font-light text-stone-900 tracking-tight">
        Four disciplines. <br /> One studio.
      </h1>
      <p className="mt-6 text-stone-600 max-w-2xl leading-relaxed">
        Every service at Physiofit is designed to work with the others. Move from acute care to long-term strength — without changing therapists or centres.
      </p>

      <div className="mt-16 space-y-16">
        {LIST.map((s, i) => {
          const Icon = s.icon;
          const flip = i % 2 === 1;
          return (
            <section key={s.key} data-testid={`service-detail-${s.key}`} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${flip ? "" : ""}`}>
              <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
                <div className="aspect-[4/3] border border-stone-200 overflow-hidden">
                  <img src={s.img} alt={s.title} className="w-full h-full object-cover" />
                </div>
              </div>
              <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                <div className="flex items-center gap-3">
                  <span className="p-2 border border-stone-200 text-emerald-900"><Icon size={18} /></span>
                  <div className="eyebrow">0{i+1} · {s.title}</div>
                </div>
                <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-light text-stone-900 leading-tight">{s.title}</h2>
                <p className="mt-5 text-stone-600 leading-relaxed">{s.desc}</p>
                <ul className="mt-6 space-y-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-stone-700">
                      <Check size={16} className="text-emerald-900 mt-1 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/book" data-testid={`services-book-${s.key}`} className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-900 text-stone-50 hover:bg-emerald-950 transition-colors duration-200">
                  Book {s.title} <ArrowUpRight size={16} />
                </Link>
              </div>
            </section>
          );
        })}
      </div>
    </main>
  );
}
